import { useEffect, useLayoutEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

const DRAG_DAMPING    = 0.38
const SWIPE_THRESHOLD = 60
const LOCK_PX         = 10
const SPRING          = 'cubic-bezier(0.25,0.46,0.45,0.94)'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isInsideInteractive(el: Element | null): boolean {
  while (el && el !== document.body) {
    if (el.tagName === 'TABLE') return true
    const role = el.getAttribute('role')
    if (role === 'slider' || role === 'grid' || role === 'gridcell' || role === 'row') return true
    if ((el as HTMLElement).dataset.noSwipe !== undefined) return true
    el = el.parentElement
  }
  return false
}

function isScrollConsumed(el: Element | null, swipingLeft: boolean): boolean {
  while (el && el !== document.body) {
    const { overflowX } = window.getComputedStyle(el)
    if (overflowX === 'auto' || overflowX === 'scroll') {
      const atRight = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1
      const atLeft  = el.scrollLeft <= 0
      if (swipingLeft  && !atRight) return true
      if (!swipingLeft && !atLeft)  return true
    }
    el = el.parentElement
  }
  return false
}

type SwipeCallback = (dir: 'left' | 'right', toPath: string) => void

/**
 * Drives one direct-DOM animation during a swipe gesture:
 *   • `draggableRef` — content follows finger via translateX (no React state → 60 fps)
 *
 * The bottom-nav indicator is intentionally NOT controlled here.
 * It fades in place via CSS `key`-remounting (see Layout.tsx) — no sliding.
 */
export function useSwipeNavigation(
  containerRef:  React.RefObject<HTMLElement | null>,
  pages:         string[],
  onSwipe:       SwipeCallback,
  draggableRef?: React.RefObject<HTMLElement | null>,
) {
  const navigate = useNavigate()
  const location = useLocation()

  const stateRef = useRef({ pages, pathname: location.pathname, navigate, onSwipe })
  useLayoutEffect(() => {
    stateRef.current = { pages, pathname: location.pathname, navigate, onSwipe }
  })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let startX:    number         = 0
    let startY:    number         = 0
    let startEl:   Element | null = null
    let dirLocked: 'h' | 'v' | null = null

    const resetContent = (immediate: boolean) => {
      const c = draggableRef?.current
      if (!c) return
      c.style.transition = immediate ? 'none' : `transform 0.3s ${SPRING}`
      c.style.transform  = ''
    }

    const onTouchStart = (e: TouchEvent) => {
      startX    = e.touches[0].clientX
      startY    = e.touches[0].clientY
      startEl   = e.target as Element
      dirLocked = null
      const c = draggableRef?.current
      if (c) c.style.transition = 'none'
    }

    const onTouchMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - startX
      const dy = e.touches[0].clientY - startY

      if (dirLocked === null && (Math.abs(dx) > LOCK_PX || Math.abs(dy) > LOCK_PX)) {
        dirLocked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
      }
      if (dirLocked !== 'h' || isInsideInteractive(startEl)) return

      const c = draggableRef?.current
      if (c) c.style.transform = `translateX(${dx * DRAG_DAMPING}px)`

      if (!isScrollConsumed(startEl, dx < 0)) e.preventDefault()
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (dirLocked !== 'h' || isInsideInteractive(startEl)) {
        resetContent(false)
        return
      }

      const dx = e.changedTouches[0].clientX - startX

      if (Math.abs(dx) < SWIPE_THRESHOLD || isScrollConsumed(startEl, dx < 0)) {
        resetContent(false)
        return
      }

      const { pages: ps, pathname, navigate: nav, onSwipe: cb } = stateRef.current
      const idx = ps.indexOf(pathname)
      if (idx === -1) { resetContent(false); return }

      if (dx < 0 && idx < ps.length - 1) {
        resetContent(true)
        cb('left', ps[idx + 1])
        nav(ps[idx + 1])
      } else if (dx > 0 && idx > 0) {
        resetContent(true)
        cb('right', ps[idx - 1])
        nav(ps[idx - 1])
      } else {
        resetContent(false)
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove',  onTouchMove,  { passive: false })
    el.addEventListener('touchend',   onTouchEnd,   { passive: true })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove',  onTouchMove)
      el.removeEventListener('touchend',   onTouchEnd)
    }
  }, [containerRef, draggableRef])
}
